/**
 * Helpdesk Operational User Guide
 * ================================
 * Full operational manual for PHS Internal Helpdesk Operations.
 * Commercial Pilot Edition - Version 1.0
 * 
 * Route: /wyldecrest/helpdesk/user-guide
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PHSCommandLayout } from '@/components/phs/enterprise';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ChevronLeft, 
  Printer, 
  Search, 
  Clock, 
  BookOpen,
  LogIn,
  LayoutDashboard,
  PlusCircle,
  Users,
  MessageSquare,
  FileText,
  Camera,
  Calendar,
  CheckCircle,
  Receipt,
  Link2,
  AlertTriangle,
  LayoutGrid,
  DollarSign,
  Shield,
  Phone,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  Image
} from 'lucide-react';

// Screenshot placeholder component
const ScreenshotPlaceholder = ({ label, id }) => (
  <div 
    className="my-6 bg-slate-800/50 border-2 border-dashed border-slate-600 rounded-xl p-8 text-center"
    data-testid={`screenshot-${id}`}
  >
    <Image className="h-12 w-12 text-slate-500 mx-auto mb-3" />
    <p className="text-slate-400 font-medium">[Screenshot Placeholder]</p>
    <p className="text-slate-500 text-sm mt-1">{label}</p>
  </div>
);

// Section component with anchor
const GuideSection = ({ id, number, title, icon: Icon, children }) => (
  <section id={id} className="mb-12 scroll-mt-24">
    <div className="flex items-center gap-3 mb-6 pb-3 border-b border-slate-700/50">
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-900/30 text-emerald-400 font-bold">
        {number}
      </div>
      {Icon && <Icon className="h-6 w-6 text-emerald-400" />}
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
    <div className="prose prose-invert prose-slate max-w-none">
      {children}
    </div>
  </section>
);

// Subsection component
const SubSection = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-lg font-semibold text-white mb-3">{title}</h3>
    {children}
  </div>
);

// Step component
const Step = ({ number, title, children }) => (
  <div className="mb-6 pl-4 border-l-2 border-emerald-600">
    <div className="flex items-center gap-2 mb-2">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-sm font-bold">
        {number}
      </span>
      <h4 className="text-white font-semibold">{title}</h4>
    </div>
    <div className="text-slate-300 ml-8">{children}</div>
  </div>
);

// Bullet list
const BulletList = ({ items, color = "emerald" }) => (
  <ul className="space-y-2 my-3">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-2 text-slate-300">
        <span className={`text-${color}-400 mt-1`}>•</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

// Info callout
const InfoBox = ({ children }) => (
  <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4 my-4 text-blue-200">
    {children}
  </div>
);

// Warning callout
const WarningBox = ({ children }) => (
  <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 my-4 text-amber-200">
    {children}
  </div>
);

// Table of contents sections
const tocSections = [
  { id: 'introduction', number: 1, title: 'Introduction' },
  { id: 'philosophy', number: 2, title: 'Helpdesk Philosophy' },
  { id: 'login', number: 3, title: 'Logging Into The Platform' },
  { id: 'dashboard', number: 4, title: 'Helpdesk Dashboard Overview' },
  { id: 'new-job', number: 5, title: 'Logging A New Job' },
  { id: 'assign-contractor', number: 6, title: 'Assigning A Contractor' },
  { id: 'communications', number: 7, title: 'Managing Communications' },
  { id: 'quote-workflow', number: 8, title: 'Quote Workflow' },
  { id: 'evidence-workflow', number: 9, title: 'Evidence Workflow' },
  { id: 'scheduling', number: 10, title: 'Scheduling Works' },
  { id: 'completion', number: 11, title: 'Completion Review' },
  { id: 'invoice-po', number: 12, title: 'Invoice & PO Governance' },
  { id: 'follow-on', number: 13, title: 'Follow-On Jobs' },
  { id: 'sla-escalation', number: 14, title: 'SLA & Escalation Handling' },
  { id: 'board-view', number: 15, title: 'Operational Board View' },
  { id: 'benchmarking', number: 16, title: 'Benchmarking & Rate Review' },
  { id: 'ppm-compliance', number: 17, title: 'PPM & Compliance Workflows' },
  { id: 'resident-comms', number: 18, title: 'Resident Communication Standards' },
  { id: 'scenarios', number: 19, title: 'Common Operational Scenarios' },
  { id: 'troubleshooting', number: 20, title: 'Troubleshooting' },
];

export default function HelpdeskUserGuidePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedToc, setExpandedToc] = useState(true);

  const filteredSections = tocSections.filter(s =>
    s.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <PHSCommandLayout>
      <div className="min-h-screen bg-[#0f1a19]" data-testid="helpdesk-user-guide">
        {/* Header */}
        <div className="border-b border-slate-700/50 bg-[#162624] px-6 py-6 print:bg-white print:border-gray-200">
          <div className="max-w-5xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-4 print:text-gray-600">
              <Link to="/wyldecrest/helpdesk/governance" className="hover:text-emerald-400 transition flex items-center gap-1">
                <ChevronLeft className="h-4 w-4" />
                Governance Hub
              </Link>
              <span>/</span>
              <span className="text-white print:text-black">Helpdesk User Guide</span>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-emerald-900/30 print:bg-emerald-100">
                  <BookOpen className="h-6 w-6 text-emerald-400 print:text-emerald-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white print:text-black">
                    Helpdesk Operational User Guide
                  </h1>
                  <p className="text-slate-400 mt-1 print:text-gray-600">
                    Internal Helpdesk Operations Manual
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-3 print:hidden">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Search sections..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 w-56"
                  />
                </div>
                <button
                  onClick={() => window.print()}
                  className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 transition"
                  title="Print Guide"
                >
                  <Printer className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm print:text-gray-600">
              <Badge variant="outline" className="bg-emerald-500/20 text-emerald-300 border-emerald-500/50 print:bg-emerald-100 print:text-emerald-700">
                Version 1.0
              </Badge>
              <span className="text-slate-500 print:text-gray-500">
                Commercial Pilot Edition
              </span>
              <span className="text-slate-600">|</span>
              <span className="text-slate-500 flex items-center gap-1 print:text-gray-500">
                <Clock className="h-4 w-4" />
                Last Updated: May 2026
              </span>
            </div>
          </div>
        </div>

        {/* Content with TOC sidebar */}
        <div className="max-w-7xl mx-auto px-6 py-8 flex gap-8">
          {/* Table of Contents - Sticky Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0 print:hidden">
            <div className="sticky top-24">
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
                <button 
                  onClick={() => setExpandedToc(!expandedToc)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3"
                >
                  Contents
                  {expandedToc ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {expandedToc && (
                  <nav className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {filteredSections.map((section) => (
                      <a
                        key={section.id}
                        href={`#${section.id}`}
                        className="block px-2 py-1.5 text-sm text-slate-400 hover:text-emerald-400 hover:bg-slate-700/30 rounded transition"
                      >
                        <span className="text-slate-600 mr-2">{section.number}.</span>
                        {section.title}
                      </a>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Title Page for Print */}
            <div className="hidden print:block text-center py-16 border-b border-gray-200 mb-8">
              <h1 className="text-4xl font-bold text-black mb-4">Park Home Support (PHS)</h1>
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">Helpdesk Operational User Guide</h2>
              <p className="text-gray-500">Internal Helpdesk Operations Manual</p>
              <p className="text-gray-400 mt-4">Version 1.0 — Commercial Pilot Edition</p>
            </div>

            {/* Section 1: Introduction */}
            <GuideSection id="introduction" number={1} title="Introduction" icon={BookOpen}>
              <SubSection title="Purpose Of This Guide">
                <p className="text-slate-300 mb-4">
                  This guide explains the full operational lifecycle of a job within the Park Home Support platform.
                </p>
                <p className="text-slate-300 mb-4">It is designed for:</p>
                <BulletList items={[
                  'Internal helpdesk staff',
                  'Operations coordinators',
                  'Dispatch staff',
                  'Operational managers',
                  'Client-side operational users'
                ]} />
                <p className="text-slate-300 mt-4">The objective of the platform is:</p>
                <BulletList items={[
                  'Minimal clicks',
                  'Operational clarity',
                  'Workflow visibility',
                  'Contractor accountability',
                  'Evidence governance',
                  'Scalable operational coordination'
                ]} />
              </SubSection>
            </GuideSection>

            {/* Section 2: Philosophy */}
            <GuideSection id="philosophy" number={2} title="Helpdesk Philosophy" icon={Shield}>
              <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-xl p-6 mb-6">
                <p className="text-emerald-300 text-lg font-semibold mb-4">
                  The helpdesk is not a call centre.
                </p>
                <p className="text-slate-300 mb-3">The helpdesk acts as:</p>
                <BulletList items={[
                  'Operational coordination centre',
                  'Workflow controller',
                  'Contractor coordination layer',
                  'Operational governance function'
                ]} />
              </div>
              <p className="text-slate-300 mb-3">The system is designed to feel:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Fast', 'Operational', 'Visible', 'Actionable'].map(item => (
                  <div key={item} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 text-center">
                    <span className="text-emerald-400 font-semibold">{item}</span>
                  </div>
                ))}
              </div>
            </GuideSection>

            {/* Section 3: Login */}
            <GuideSection id="login" number={3} title="Logging Into The Platform" icon={LogIn}>
              <SubSection title="Accessing The Helpdesk">
                <p className="text-slate-300 mb-4">Navigate to: <span className="text-emerald-400 font-mono">PHS Internal Operations Portal</span></p>
                <p className="text-slate-300 mb-4">Log in using your:</p>
                <BulletList items={['Email address', 'Password']} />
                <p className="text-slate-300 mt-4">The system uses:</p>
                <BulletList items={[
                  'Secure session authentication',
                  'Role-based access',
                  'Operational audit logging'
                ]} />
              </SubSection>
              <ScreenshotPlaceholder label="Login Screen" id="login-screen" />
            </GuideSection>

            {/* Section 4: Dashboard */}
            <GuideSection id="dashboard" number={4} title="Helpdesk Dashboard Overview" icon={LayoutDashboard}>
              <SubSection title="Main Areas">
                <p className="text-slate-300 mb-4">The helpdesk dashboard consists of:</p>
                
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 mb-4">
                  <h4 className="text-white font-semibold mb-2">Sidebar Navigation</h4>
                  <p className="text-slate-400 text-sm mb-2">Contains:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-300">
                    <span>• Command Centre</span>
                    <span>• Job Lifecycle</span>
                    <span>• Operational Board</span>
                    <span>• PPM & Compliance</span>
                    <span>• Assets</span>
                    <span>• Commercial Intelligence</span>
                    <span>• Governance</span>
                    <span>• Reporting</span>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">Main Workspace</h4>
                  <p className="text-slate-400 text-sm mb-2">Displays:</p>
                  <BulletList items={[
                    'Active jobs',
                    'Operational KPIs',
                    'SLA warnings',
                    'Workflow statuses',
                    'Quick operational actions'
                  ]} />
                </div>
              </SubSection>
              <ScreenshotPlaceholder label="Helpdesk Dashboard" id="helpdesk-dashboard" />
            </GuideSection>

            {/* Section 5: New Job */}
            <GuideSection id="new-job" number={5} title="Logging A New Job" icon={PlusCircle}>
              <Step number={1} title="Create New Job">
                <p>Select <span className="text-emerald-400 font-semibold">"New Job"</span> from:</p>
                <BulletList items={[
                  'Dashboard quick actions',
                  'Sidebar',
                  'Command centre'
                ]} />
              </Step>
              <ScreenshotPlaceholder label="New Job Button" id="new-job-button" />

              <Step number={2} title="Enter Resident Information">
                <p className="mb-2">Input:</p>
                <BulletList items={[
                  'Resident name',
                  'Contact number',
                  'Email address',
                  'Park name',
                  'Plot number'
                ]} />
                <p className="mt-3 mb-2">Confirm:</p>
                <BulletList items={[
                  'Correct site',
                  'Correct plot',
                  'Access notes where applicable'
                ]} />
              </Step>
              <ScreenshotPlaceholder label="Resident Details Form" id="resident-details" />

              <Step number={3} title="Enter Job Details">
                <p className="mb-2">Add:</p>
                <BulletList items={[
                  'Issue description',
                  'Operational notes',
                  'Category',
                  'Priority level',
                  'Emergency indicators'
                ]} />
                <p className="mt-3 text-slate-400 text-sm">Examples: Plumbing leak, Drainage issue, Electrical fault, Base movement, Skirting repair, Roofing issue</p>
              </Step>
              <ScreenshotPlaceholder label="Job Details Section" id="job-details" />

              <Step number={4} title="Upload Initial Evidence">
                <p className="mb-2">Upload:</p>
                <BulletList items={[
                  'Resident photographs',
                  'Inspection images',
                  'Documents',
                  'Supporting files'
                ]} />
                <InfoBox>
                  The platform supports: drag & drop, camera upload, mobile uploads, and multi-file upload.
                </InfoBox>
              </Step>
              <ScreenshotPlaceholder label="Evidence Upload Area" id="evidence-upload" />

              <Step number={5} title="Save Job">
                <p>Once saved:</p>
                <BulletList items={[
                  'A job reference is generated',
                  'The workflow status is assigned',
                  'The job enters operational lifecycle management'
                ]} />
              </Step>
              <ScreenshotPlaceholder label="Saved Job Card" id="saved-job-card" />
            </GuideSection>

            {/* Section 6: Assign Contractor */}
            <GuideSection id="assign-contractor" number={6} title="Assigning A Contractor" icon={Users}>
              <SubSection title="Contractor Assignment Workflow">
                <ol className="space-y-2 text-slate-300">
                  <li>1. Open the Job Workspace</li>
                  <li>2. Select <span className="text-emerald-400 font-semibold">"Assign Contractor"</span></li>
                  <li>3. Choose:
                    <BulletList items={[
                      'Contractor company',
                      'Engineer if applicable',
                      'Operational notes',
                      'Target response times'
                    ]} />
                  </li>
                </ol>
              </SubSection>
              <ScreenshotPlaceholder label="Assign Contractor Panel" id="assign-contractor" />

              <SubSection title="Assignment Outcomes">
                <p className="text-slate-300 mb-3">The system will:</p>
                <BulletList items={[
                  'Notify the contractor',
                  'Create audit entries',
                  'Update workflow statuses',
                  'Trigger SLA tracking'
                ]} />
              </SubSection>

              <SubSection title="Contractor Statuses">
                <p className="text-slate-300 mb-3">Typical statuses include:</p>
                <div className="flex flex-wrap gap-2">
                  {['Assigned', 'Awaiting Quote', 'Quote Submitted', 'Scheduled', 'In Progress', 'Completed Awaiting Review'].map(status => (
                    <Badge key={status} variant="outline" className="bg-slate-800/50 text-slate-300 border-slate-600">
                      {status}
                    </Badge>
                  ))}
                </div>
              </SubSection>
            </GuideSection>

            {/* Section 7: Communications */}
            <GuideSection id="communications" number={7} title="Managing Communications" icon={MessageSquare}>
              <SubSection title="Communications Hub">
                <p className="text-slate-300 mb-4">
                  The centre panel of the Job Workspace contains the Communications Hub.
                </p>
                <p className="text-slate-300 mb-3">This centralises:</p>
                <BulletList items={[
                  'Internal notes',
                  'Resident updates',
                  'Contractor updates',
                  'Escalation notes',
                  'Operational commentary'
                ]} />
              </SubSection>

              <SubSection title="Quick Communication Actions">
                <p className="text-slate-300 mb-3">Available actions include:</p>
                <div className="flex flex-wrap gap-2">
                  {['WhatsApp', 'Call', 'Email', 'Chase Contractor', 'Resident Update'].map(action => (
                    <Badge key={action} className="bg-emerald-900/30 text-emerald-300 border-emerald-700/50">
                      {action}
                    </Badge>
                  ))}
                </div>
              </SubSection>
              <ScreenshotPlaceholder label="Communications Hub" id="communications-hub" />

              <SubSection title="Best Practice">
                <p className="text-slate-300 mb-3">Communications should:</p>
                <BulletList items={[
                  'Remain professional',
                  'Remain operationally useful',
                  'Avoid duplication',
                  'Clearly identify actions required'
                ]} />
              </SubSection>
            </GuideSection>

            {/* Section 8: Quote Workflow */}
            <GuideSection id="quote-workflow" number={8} title="Quote Workflow" icon={FileText}>
              <SubSection title="Contractor Quote Submission">
                <p className="text-slate-300 mb-3">Contractors may submit:</p>
                <BulletList items={[
                  'Labour pricing',
                  'Materials',
                  'Line items',
                  'Scope descriptions',
                  'Supporting evidence'
                ]} />
              </SubSection>

              <SubSection title="Quote Review Process">
                <p className="text-slate-300 mb-3">Helpdesk/operations should review:</p>
                <BulletList items={[
                  'Pricing reasonableness',
                  'Benchmark comparisons',
                  'Scope clarity',
                  'Evidence quality'
                ]} />
                <p className="text-slate-300 mt-4 mb-3">Possible outcomes:</p>
                <div className="flex gap-3">
                  <Badge className="bg-emerald-600 text-white">Approve</Badge>
                  <Badge className="bg-red-600 text-white">Reject</Badge>
                  <Badge className="bg-amber-600 text-white">Request Revision</Badge>
                </div>
              </SubSection>
              <ScreenshotPlaceholder label="Quote Review Panel" id="quote-review" />
            </GuideSection>

            {/* Section 9: Evidence Workflow */}
            <GuideSection id="evidence-workflow" number={9} title="Evidence Workflow" icon={Camera}>
              <SubSection title="Evidence Categories">
                <p className="text-slate-300 mb-3">The platform supports:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['Before Photos', 'During Photos', 'After Photos', 'SAT Notes', 'Certificates', 'Quotes', 'Invoices'].map(cat => (
                    <div key={cat} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-2 text-center text-sm text-slate-300">
                      {cat}
                    </div>
                  ))}
                </div>
              </SubSection>

              <SubSection title="Evidence Rules">
                <p className="text-slate-300 mb-3">The platform may enforce:</p>
                <BulletList items={[
                  'Mandatory uploads',
                  'Workflow prompts',
                  'Invoice blocking',
                  'Review workflows'
                ]} />
              </SubSection>

              <SubSection title="Evidence Review">
                <p className="text-slate-300 mb-3">Internal operations may:</p>
                <BulletList items={[
                  'Approve evidence',
                  'Reject evidence',
                  'Request further uploads'
                ]} />
              </SubSection>
              <ScreenshotPlaceholder label="Evidence Review Panel" id="evidence-review" />
            </GuideSection>

            {/* Section 10: Scheduling */}
            <GuideSection id="scheduling" number={10} title="Scheduling Works" icon={Calendar}>
              <SubSection title="Scheduling Process">
                <p className="text-slate-300 mb-3">Once works are approved:</p>
                <BulletList items={[
                  'Contractor attendance dates are confirmed',
                  'Resident communication is updated',
                  'Workflow status changes to Scheduled'
                ]} />
              </SubSection>
              <ScreenshotPlaceholder label="Scheduling Panel" id="scheduling-panel" />
            </GuideSection>

            {/* Section 11: Completion */}
            <GuideSection id="completion" number={11} title="Completion Review" icon={CheckCircle}>
              <SubSection title="Completion Checks">
                <p className="text-slate-300 mb-3">Before closure, verify:</p>
                <BulletList items={[
                  'After photographs uploaded',
                  'SAT note completed',
                  'Certificates uploaded',
                  'Resident issues resolved',
                  'Contractor notes completed'
                ]} />
              </SubSection>

              <SubSection title="Workflow Outcomes">
                <p className="text-slate-300 mb-3">Possible outcomes:</p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-emerald-600 text-white">Approve Completion</Badge>
                  <Badge className="bg-amber-600 text-white">Request More Evidence</Badge>
                  <Badge className="bg-blue-600 text-white">Raise Follow-On Job</Badge>
                  <Badge className="bg-red-600 text-white">Escalate Internally</Badge>
                </div>
              </SubSection>
            </GuideSection>

            {/* Section 12: Invoice & PO */}
            <GuideSection id="invoice-po" number={12} title="Invoice & PO Governance" icon={Receipt}>
              <SubSection title="Invoice Rules">
                <WarningBox>
                  <p className="font-semibold mb-2">Invoices will NOT be approved where:</p>
                  <BulletList items={[
                    'Evidence missing',
                    'SAT note missing',
                    'Certificates missing',
                    'No PO exists',
                    'Operational review incomplete'
                  ]} color="amber" />
                </WarningBox>
              </SubSection>

              <SubSection title="PO Workflow">
                <p className="text-slate-300 mb-3">Approved jobs generate:</p>
                <BulletList items={[
                  'Operational approvals',
                  'PO references',
                  'Financial visibility'
                ]} />
              </SubSection>
              <ScreenshotPlaceholder label="Invoice Review Panel" id="invoice-review" />
            </GuideSection>

            {/* Section 13: Follow-On Jobs */}
            <GuideSection id="follow-on" number={13} title="Follow-On Jobs" icon={Link2}>
              <SubSection title="Purpose">
                <p className="text-slate-300 mb-3">Follow-On Jobs are used when:</p>
                <BulletList items={[
                  'Additional works identified',
                  'Further investigation required',
                  'Specialist repairs required',
                  'Operational scope expands'
                ]} />
                <InfoBox>
                  The Follow-On Job remains linked to: original job history, communications, evidence, and operational audit trail.
                </InfoBox>
              </SubSection>
              <ScreenshotPlaceholder label="Follow-On Job Creation" id="follow-on-job" />
            </GuideSection>

            {/* Section 14: SLA & Escalation */}
            <GuideSection id="sla-escalation" number={14} title="SLA & Escalation Handling" icon={AlertTriangle}>
              <SubSection title="SLA Visibility">
                <p className="text-slate-300 mb-3">The platform displays:</p>
                <BulletList items={[
                  'SLA timers',
                  'Colour indicators',
                  'Overdue warnings',
                  'Escalation visibility'
                ]} />
              </SubSection>

              <SubSection title="Escalation Examples">
                <p className="text-slate-300 mb-3">Escalate where:</p>
                <BulletList items={[
                  'Contractors unresponsive',
                  'Evidence incomplete',
                  'Emergency risk exists',
                  'Resident vulnerable',
                  'Repeated operational failures occur'
                ]} />
              </SubSection>
              <ScreenshotPlaceholder label="SLA Warning Indicators" id="sla-warnings" />
            </GuideSection>

            {/* Section 15: Board View */}
            <GuideSection id="board-view" number={15} title="Operational Board View" icon={LayoutGrid}>
              <SubSection title="Purpose">
                <p className="text-slate-300 mb-3">The Operational Board View provides:</p>
                <BulletList items={[
                  'Full workflow visibility',
                  'Operational bottleneck visibility',
                  'Contractor tracking',
                  'Status progression'
                ]} />
              </SubSection>

              <SubSection title="Typical Workflow Columns">
                <div className="flex flex-wrap gap-2">
                  {[
                    'New Enquiries', 'Assigned', 'Awaiting Quote', 'Awaiting Approval',
                    'Scheduled', 'In Progress', 'Completed Awaiting Review',
                    'Awaiting Invoice', 'Closed', 'Cancelled'
                  ].map(col => (
                    <Badge key={col} variant="outline" className="bg-slate-800/50 text-slate-300 border-slate-600">
                      {col}
                    </Badge>
                  ))}
                </div>
              </SubSection>
              <ScreenshotPlaceholder label="Operational Board View" id="board-view" />
            </GuideSection>

            {/* Section 16: Benchmarking */}
            <GuideSection id="benchmarking" number={16} title="Benchmarking & Rate Review" icon={DollarSign}>
              <SubSection title="Commercial Intelligence">
                <p className="text-slate-300 mb-3">The platform contains:</p>
                <BulletList items={[
                  'Benchmark pricing',
                  'Average costs',
                  'Contractor rate visibility',
                  'Cost comparison tools'
                ]} />
                <p className="text-slate-300 mt-4 mb-3">Used to:</p>
                <BulletList items={[
                  'Validate pricing',
                  'Identify anomalies',
                  'Support operational review'
                ]} />
              </SubSection>
              <ScreenshotPlaceholder label="Benchmarking Page" id="benchmarking" />
            </GuideSection>

            {/* Section 17: PPM & Compliance */}
            <GuideSection id="ppm-compliance" number={17} title="PPM & Compliance Workflows" icon={Shield}>
              <SubSection title="PPM Management">
                <p className="text-slate-300 mb-3">The platform supports:</p>
                <BulletList items={[
                  'Planned preventative maintenance',
                  'Compliance scheduling',
                  'Expiry tracking',
                  'Operational reminders'
                ]} />
                <p className="text-slate-300 mt-4 mb-3">Examples include:</p>
                <div className="flex flex-wrap gap-2">
                  {['EICR', 'Gas Safety', 'Fire Checks', 'Legionella'].map(item => (
                    <Badge key={item} className="bg-purple-900/30 text-purple-300 border-purple-700/50">
                      {item}
                    </Badge>
                  ))}
                </div>
              </SubSection>
              <ScreenshotPlaceholder label="PPM Library" id="ppm-library" />
            </GuideSection>

            {/* Section 18: Resident Communication */}
            <GuideSection id="resident-comms" number={18} title="Resident Communication Standards" icon={Phone}>
              <SubSection title="Resident Communication Principles">
                <p className="text-slate-300 mb-3">Residents should:</p>
                <BulletList items={[
                  'Receive clear updates',
                  'Receive realistic timelines',
                  'Understand next steps',
                  'Feel operationally supported'
                ]} />
                <WarningBox>
                  <p className="font-semibold mb-2">Avoid:</p>
                  <BulletList items={[
                    'Technical jargon',
                    'Vague updates',
                    'Unconfirmed commitments'
                  ]} color="amber" />
                </WarningBox>
              </SubSection>
            </GuideSection>

            {/* Section 19: Common Scenarios */}
            <GuideSection id="scenarios" number={19} title="Common Operational Scenarios" icon={HelpCircle}>
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">Scenario — Contractor Non-Response</h4>
                  <p className="text-slate-400 text-sm mb-2">Actions:</p>
                  <BulletList items={[
                    'Issue chase communication',
                    'Escalate internally if overdue',
                    'Consider reassignment',
                    'Log operational notes'
                  ]} />
                </div>

                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">Scenario — Missing Evidence</h4>
                  <p className="text-slate-400 text-sm mb-2">Actions:</p>
                  <BulletList items={[
                    'Request additional evidence',
                    'Block invoice if required',
                    'Escalate repeated non-compliance'
                  ]} />
                </div>

                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">Scenario — Emergency Leak</h4>
                  <p className="text-slate-400 text-sm mb-2">Actions:</p>
                  <BulletList items={[
                    'Prioritise emergency attendance',
                    'Escalate immediately',
                    'Maintain resident communication',
                    'Ensure evidence captured'
                  ]} />
                </div>
              </div>
            </GuideSection>

            {/* Section 20: Troubleshooting */}
            <GuideSection id="troubleshooting" number={20} title="Troubleshooting" icon={HelpCircle}>
              <SubSection title="Common Issues">
                <div className="space-y-4">
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-2">Upload Failures</h4>
                    <p className="text-slate-400 text-sm mb-2">Recommended actions:</p>
                    <BulletList items={[
                      'Retry upload',
                      'Verify signal/connectivity',
                      'Refresh workspace',
                      'Escalate repeated failures'
                    ]} />
                  </div>

                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-2">Session Issues</h4>
                    <p className="text-slate-400 text-sm mb-2">Recommended actions:</p>
                    <BulletList items={[
                      'Re-login',
                      'Verify permissions',
                      'Clear stale sessions'
                    ]} />
                  </div>

                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-2">Workflow Blocking</h4>
                    <p className="text-slate-400 text-sm mb-2">Check:</p>
                    <BulletList items={[
                      'Evidence requirements',
                      'SAT note completion',
                      'Workflow status',
                      'Permissions'
                    ]} />
                  </div>
                </div>
              </SubSection>
            </GuideSection>

            {/* Final Notes */}
            <div className="mt-12 pt-8 border-t border-slate-700/50">
              <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-white mb-4">Final Notes</h3>
                <p className="text-slate-300 mb-4">
                  Park Home Support is designed as <span className="text-emerald-400 font-semibold">specialist operational infrastructure</span>, not generic maintenance software.
                </p>
                <p className="text-slate-300 mb-6">The operational focus should always remain:</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {['Fast', 'Clear', 'Accountable', 'Evidence-led', 'Operationally Scalable'].map(item => (
                    <Badge key={item} className="bg-emerald-600 text-white px-4 py-1">
                      {item}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 text-center text-slate-500 text-sm">
                <p className="font-semibold text-slate-400">Park Home Support</p>
                <p>Managing Park Estates · Supporting People</p>
                <p className="mt-2">
                  advice@parkhomesupport.co.uk | www.parkhomesupport.co.uk | 07557 017694
                </p>
              </div>
            </div>
          </main>
        </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            body { background: white !important; }
            .bg-\\[\\#0f1a19\\] { background: white !important; }
            .bg-\\[\\#162624\\] { background: #f3f4f6 !important; }
            .bg-slate-800\\/30, .bg-slate-800\\/50, .bg-slate-900\\/50 { 
              background: #f9fafb !important; 
              border-color: #e5e7eb !important; 
            }
            .bg-emerald-900\\/20, .bg-emerald-900\\/30 { 
              background: #ecfdf5 !important; 
              border-color: #a7f3d0 !important;
            }
            .text-white { color: black !important; }
            .text-slate-300, .text-slate-400, .text-slate-500 { color: #4b5563 !important; }
            .text-emerald-400, .text-emerald-300 { color: #059669 !important; }
            button, input, aside { display: none !important; }
            section { page-break-inside: avoid; }
            .print\\:hidden { display: none !important; }
            .print\\:block { display: block !important; }
            .print\\:bg-white { background: white !important; }
            .print\\:text-black { color: black !important; }
          }
          
          @page {
            size: A4;
            margin: 1.5cm;
          }
        `}</style>
      </div>
    </PHSCommandLayout>
  );
}
